import type { SweepDrawConfig } from '@ui/Types/Gnss';

import { getSweepEffectiveLeftPad } from './layout';

const SWEEP_FREQUENCY_EPSILON = 1e-6;

export interface SweepFrequencyDisplayUnit {
    readonly factorFromMHz: number;
    readonly label: 'Hz' | 'kHz' | 'MHz';
}

export type SweepTraceMode = 'latest' | 'average' | 'peak';

export interface SweepTraceSamples {
    average?: ArrayLike<number> | null;
    baseline?: ArrayLike<number> | null;
    latest?: ArrayLike<number> | null;
    peak?: ArrayLike<number> | null;
}

export interface SweepCursorFrequency {
    binIndex: number | null;
    contentX: number;
    frequencyMHz: number;
    stepMHz: number;
}

function clampFrequencyMHz(frequencyMHz: number, totalMHz: number): number {
    return Math.max(0, Math.min(totalMHz, frequencyMHz));
}

function getContinuousFrequencyStepMHz(pxPerMHz: number): number {
    return Math.max(SWEEP_FREQUENCY_EPSILON, 1 / Math.max(SWEEP_FREQUENCY_EPSILON, pxPerMHz));
}

function isValidSweepDrawConfig(drawConfig: SweepDrawConfig | null | undefined): drawConfig is SweepDrawConfig {
    return Boolean(
        drawConfig &&
        Number.isFinite(drawConfig.freqStartMHz) &&
        Number.isFinite(drawConfig.freqEndMHz) &&
        drawConfig.freqEndMHz > drawConfig.freqStartMHz &&
        Number.isFinite(drawConfig.binWidthMHz) &&
        drawConfig.binWidthMHz > 0,
    );
}

export function getSweepFrequencyDisplayUnit(params: {
    stepMHz: number;
    visibleEndMHz: number;
    visibleStartMHz: number;
}): SweepFrequencyDisplayUnit {
    const { stepMHz, visibleEndMHz } = params;

    if (visibleEndMHz < 2 && stepMHz < 0.001) {
        return { factorFromMHz: 1_000_000, label: 'Hz' };
    }

    if (visibleEndMHz < 20 && stepMHz < 1) {
        return { factorFromMHz: 1000, label: 'kHz' };
    }

    return { factorFromMHz: 1, label: 'MHz' };
}

export function getSweepFrequencyLabelPrecision(stepMHz: number, factorFromMHz: number): number {
    const scaledStep = stepMHz * factorFromMHz;

    if (!Number.isFinite(scaledStep) || scaledStep <= 0) {
        return 0;
    }

    if (scaledStep >= 1) {
        return 0;
    }

    return Math.min(6, Math.max(0, Math.ceil(-Math.log10(Math.max(SWEEP_FREQUENCY_EPSILON, scaledStep)))));
}

export function formatSweepFrequencyLabel(params: {
    frequencyMHz: number;
    stepMHz: number;
    unit: SweepFrequencyDisplayUnit;
}): string {
    const { frequencyMHz, stepMHz, unit } = params;

    return (frequencyMHz * unit.factorFromMHz).toFixed(getSweepFrequencyLabelPrecision(stepMHz, unit.factorFromMHz));
}

export function getSweepSampleAtBin(
    values: ArrayLike<number> | null | undefined,
    binIndex: number | null,
): number | null {
    if (binIndex === null || binIndex < 0 || !values || binIndex >= values.length) {
        return null;
    }

    const value = values[binIndex];

    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }

    return value;
}

export function getMaximumFiniteValue(values: readonly (number | null | undefined)[]): number | null {
    let maxValue: number | null = null;

    for (const value of values) {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            continue;
        }

        if (maxValue === null || value > maxValue) {
            maxValue = value;
        }
    }

    return maxValue;
}

export function getPreferredSweepTraceMode(params: {
    hasAverage: boolean;
    hasLatest: boolean;
    hasPeak: boolean;
    showAverage: boolean;
    showLatest: boolean;
    showPeak: boolean;
}): SweepTraceMode | null {
    const { hasAverage, hasLatest, hasPeak, showAverage, showLatest, showPeak } = params;

    if (showPeak && hasPeak) {
        return 'peak';
    }

    if (showLatest && hasLatest) {
        return 'latest';
    }

    if (showAverage && hasAverage) {
        return 'average';
    }

    return null;
}

function getSweepModeValueAtBin(
    samples: SweepTraceSamples | null | undefined,
    mode: SweepTraceMode | null,
    binIndex: number | null,
): number | null | undefined {
    if (!samples || !mode) {
        return null;
    }

    switch (mode) {
        case 'latest': {
            return getSweepSampleAtBin(samples.latest, binIndex);
        }
        case 'average': {
            return getSweepSampleAtBin(samples.average, binIndex);
        }
        case 'peak': {
            return getSweepSampleAtBin(samples.peak, binIndex);
        }
        default: {
            return null;
        }
    }
}

export function getSweepTraceValueAtBin(params: {
    binIndex: number | null;
    mode: SweepTraceMode | null;
    primary?: SweepTraceSamples | null;
    secondary?: SweepTraceSamples | null;
}): number | null {
    const { binIndex, mode, primary, secondary } = params;

    if (mode) {
        const traceValue = getMaximumFiniteValue([
            getSweepModeValueAtBin(primary, mode, binIndex),
            getSweepModeValueAtBin(secondary, mode, binIndex),
        ]);

        if (traceValue !== null) {
            return traceValue;
        }
    }

    return getMaximumFiniteValue([
        getSweepSampleAtBin(primary?.baseline, binIndex),
        getSweepSampleAtBin(secondary?.baseline, binIndex),
    ]);
}

export function getSweepCursorFrequency(params: {
    contentX: number;
    drawConfig: SweepDrawConfig | null | undefined;
    leftPadPx: number;
    pxPerMHz: number;
    totalMHz: number;
}): SweepCursorFrequency {
    const { contentX, drawConfig, leftPadPx, pxPerMHz, totalMHz } = params;
    const effectiveLeftPad = getSweepEffectiveLeftPad(leftPadPx);
    const rawFrequencyMHz = clampFrequencyMHz(
        (contentX - effectiveLeftPad) / Math.max(SWEEP_FREQUENCY_EPSILON, pxPerMHz),
        totalMHz,
    );
    const rawContentX = effectiveLeftPad + rawFrequencyMHz * pxPerMHz;

    if (
        !isValidSweepDrawConfig(drawConfig) ||
        rawFrequencyMHz < drawConfig.freqStartMHz - SWEEP_FREQUENCY_EPSILON ||
        rawFrequencyMHz > drawConfig.freqEndMHz + SWEEP_FREQUENCY_EPSILON
    ) {
        return {
            binIndex: null,
            contentX: rawContentX,
            frequencyMHz: rawFrequencyMHz,
            stepMHz: getContinuousFrequencyStepMHz(pxPerMHz),
        };
    }

    const binIndex = Math.round(
        (rawFrequencyMHz - drawConfig.freqStartMHz) / Math.max(SWEEP_FREQUENCY_EPSILON, drawConfig.binWidthMHz),
    );
    const snappedFrequencyMHz = clampFrequencyMHz(
        Math.max(
            drawConfig.freqStartMHz,
            Math.min(drawConfig.freqEndMHz, drawConfig.freqStartMHz + binIndex * drawConfig.binWidthMHz),
        ),
        totalMHz,
    );

    return {
        binIndex,
        contentX: effectiveLeftPad + snappedFrequencyMHz * pxPerMHz,
        frequencyMHz: snappedFrequencyMHz,
        stepMHz: drawConfig.binWidthMHz,
    };
}
